import React, { useState, useEffect } from 'react';
import {
    Typography,
    Paper,
    Box,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    TextField,
    InputAdornment,
    Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Link } from 'react-router-dom';
import { getFirestore, collection, getDocs, Timestamp, query, where } from 'firebase/firestore';
import PeopleIcon from '@mui/icons-material/People';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import Fiananceheader from './Fiananceheader';

const db = getFirestore();

const DashboardCard = ({ title, count, icon, color, link }) => (
    <Link to={link} style={{ textDecoration: 'none' }}>
        <Paper
            elevation={4}
            sx={{
                height: 130,
                borderRadius: 2,
                p: 2,
                backgroundColor: color,
                color: '#fff',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: '0.3s',
                cursor: 'pointer',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                    opacity: 0.95,
                },
            }}
        >
            <Box>
                <Typography variant="subtitle1">{title}</Typography>
                <Typography variant="h5" fontWeight="bold">
                    {count}
                </Typography>
            </Box>
            <Box
                sx={{
                    fontSize: 50,
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    borderRadius: 2,
                    p: 1,
                    height: 70,
                    lineHeight: 1,
                }}
            >
                {icon}
            </Box>
        </Paper>
    </Link>
);

const Fianancehome = () => {
    const [counts, setCounts] = useState({
        customerCount: 0,
        loanCount: 0,
        todayLoanGiven: '₹0',
        todayEmiTaken: '₹0',
    });
    const [pendingEmis, setPendingEmis] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const startOfToday = Timestamp.fromDate(today);
                const endOfToday = Timestamp.fromDate(new Date(today.setHours(23, 59, 59, 999)));

                // Fetch all customers and create a map for quick lookup
                const customersSnapshot = await getDocs(collection(db, 'fianancecustomers'));
                const customersMap = {};
                customersSnapshot.forEach((doc) => {
                    customersMap[doc.id] = doc.data();
                });
                const customerCount = customersSnapshot.size;

                // Fetch total loan count
                const loansSnapshot = await getDocs(collection(db, 'loans'));
                const loanCount = loansSnapshot.size;

                // Fetch today's loans given
                const todayLoansQuery = query(
                    collection(db, 'loans'),
                    where('loandate', '>=', startOfToday),
                    where('loandate', '<=', endOfToday)
                );
                const todayLoansSnapshot = await getDocs(todayLoansQuery);
                let todayLoanGiven = 0;
                todayLoansSnapshot.forEach((doc) => {
                    const data = doc.data();
                    todayLoanGiven += data.loanamount || 0;
                });

                // Fetch today's EMI collection
                const todayEmiQuery = query(
                    collection(db, 'loanemis'),
                    where('paydate', '>=', startOfToday),
                    where('paydate', '<=', endOfToday),
                );
                const todayEmiSnapshot = await getDocs(todayEmiQuery);
                let todayEmiTaken = 0;
                todayEmiSnapshot.forEach((doc) => {
                    const data = doc.data();
                    todayEmiTaken += data.amount || 0; // <-- use 'amount' field, not 'amountperday'
                });


                // Fetch today's pending EMIs and enrich with customer data
                const pendingEmiQuery = query(
                    collection(db, 'loanemis'),
                    where('paydate', '<=', endOfToday),
                );
                const pendingEmiSnapshot = await getDocs(pendingEmiQuery);
                const pendingEmiList = pendingEmiSnapshot.docs.map((doc) => {
                    const emiData = doc.data();
                    const customerData = customersMap[emiData.custid] || {};

                    return {
                        id: doc.id,
                        ...emiData,
                        customerName: customerData.name || 'N/A',
                        mobileNo: customerData.mobile || 'N/A',
                    };
                });

                setCounts({
                    customerCount: customerCount,
                    loanCount: loanCount,
                    todayLoanGiven: `₹${todayLoanGiven.toLocaleString('en-IN')}`,
                    todayEmiTaken: `₹${todayEmiTaken.toLocaleString('en-IN')}`,
                });
                setPendingEmis(pendingEmiList);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
                setCounts({
                    customerCount: 'N/A',
                    loanCount: 'N/A',
                    todayLoanGiven: '₹N/A',
                    todayEmiTaken: '₹N/A',
                });
                setPendingEmis([]);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleSendWhatsApp = (emi) => {
        const mobileNumber = emi.mobileNo.startsWith('+') ? emi.mobileNo.substring(1) : emi.mobileNo;
        const formattedMobileNumber = `91${mobileNumber}`; // Assuming India's country code +91
        const formattedDate = emi.date ? new Date(emi.date.seconds * 1000).toLocaleDateString('en-IN') : 'N/A';
        const message = `Hello ${emi.customerName}, this is a friendly reminder that your EMI of ₹${emi.amountperday.toFixed(2)} was due on ${formattedDate}. Please make the payment as soon as possible. Thank you!`;
        const encodedMessage = encodeURIComponent(message);
        const whatsappUrl = `https://wa.me/${formattedMobileNumber}?text=${encodedMessage}`;

        window.open(whatsappUrl, '_blank');
    };

    const filteredEmis = pendingEmis.filter((emi) =>
        emi.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emi.mobileNo.includes(searchTerm)
    );

    return (
        <Box>
            <Fiananceheader />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                Dashboard
            </Typography>
            <Grid container className="row">
                <Grid className="col-sm-3 mb-4" item xs={12} sm={6} md={3}>
                    <DashboardCard
                        title="टोटल कर्जदार  "
                        count={counts.customerCount}
                        icon={<PeopleIcon fontSize="inherit" />}
                        color="#1976d2"
                        link="/finance/customer-account"
                    />
                </Grid>
                <Grid className="col-sm-3 mb-4" item xs={12} sm={6} md={3}>
                    <DashboardCard
                        title="टोटल कर्ज अकाउंट्स "
                        count={counts.loanCount}
                        icon={<LocalAtmIcon fontSize="inherit" />}
                        color="#6a1b9a"
                        link="/finance/loans/details"
                    />
                </Grid>
                <Grid className="col-sm-3 mb-4" item xs={12} sm={6} md={3}>
                    <DashboardCard
                        title="आजची कर्ज टोटल "
                        count={counts.todayLoanGiven}
                        icon={<LocalAtmIcon fontSize="inherit" />}
                        color="#2e7d32"
                        link="/finance/loans/given"
                    />
                </Grid>
                <Grid className="col-sm-3 mb-4" item xs={12} sm={6} md={3}>
                    <DashboardCard
                        title="आजची वसुली "
                        count={counts.todayEmiTaken}
                        icon={<AccountBalanceWalletIcon fontSize="inherit" />}
                        color="#ed6c02"
                        link="/finance/loans/emi-taken"
                    />
                </Grid>
                <Grid className="col-sm-3 mb-4" item xs={12} sm={6} md={3}>
                    <DashboardCard
                        title="Reports"
                        count="रिपोर्ट बघा "
                        icon={<AssessmentIcon fontSize="inherit" />}
                        color="#455a64"
                        link="/finance/reports/loan-report"
                    />
                </Grid>
            </Grid>

            {/* Today's Pending EMI Section */}
            {/* <Paper sx={{ p: 4, borderRadius: '5px', mt: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#d32f2f' }}>
                    📅 आजपासून मागील न भरलेली वसुली
                </Typography>
                <Box sx={{ mb: 2, mt: 3 }}>
                    <TextField
                        fullWidth
                        label="Search by Name or Mobile Number"
                        variant="outlined"
                        value={searchTerm}
                        size='small'
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell>Sr. No.</TableCell>
                                <TableCell>Action</TableCell>
                                <TableCell>कर्जदार नाव   </TableCell>
                                <TableCell>मोबाईल </TableCell>
                                <TableCell>हप्ता </TableCell>
                                <TableCell>हप्ता तारीख </TableCell>
                                <TableCell>स्टेटस </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <CircularProgress size={24} />
                                    </TableCell>
                                </TableRow>
                            ) : filteredEmis.length > 0 ? (
                                filteredEmis.map((emi, index) => (
                                    <TableRow key={emi.id}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>
                                            <Button
                                                startIcon={<WhatsAppIcon color='green' />}
                                                onClick={() => handleSendWhatsApp(emi)}
                                            >
                                            </Button>
                                        </TableCell>
                                        <TableCell>{emi.customerName}</TableCell>
                                        <TableCell>{emi.mobileNo}</TableCell>
                                        <TableCell>₹{emi.amountperday.toFixed(2)}</TableCell>
                                        <TableCell>
                                            {emi.date && new Date(emi.date.seconds * 1000).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell>{emi.status}</TableCell>

                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        No pending EMIs found. 🎉
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper> */}
        </Box>
    );
};

export default Fianancehome;